import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchLaunches,
  relativeTime,
  type LaunchRecord,
} from '../lib/launchesApi'
import { FEE_PERCENT } from '../lib/constants'

export function ExploreView({ onCreate }: { onCreate: () => void }) {
  const [launches, setLaunches] = useState<LaunchRecord[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  const load = useCallback(async () => {
    setError(null)
    try {
      setLaunches(await fetchLaunches())
    } catch (e) {
      setLaunches([])
      setError(
        e instanceof Error
          ? e.message
          : 'Could not load launches (deploy /api/launches.php?)',
      )
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = useMemo(() => {
    if (!launches) return []
    const q = query.trim().toLowerCase()
    if (!q) return launches
    return launches.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.symbol.toLowerCase().includes(q) ||
        l.mint.toLowerCase().includes(q),
    )
  }, [launches, query])

  return (
    <>
      <div className="explore-hero">
        <h1>Fees that help people move on.</h1>
        <p>
          EXFUND is a Solana launchpad for wry, redemptive meme coins. Launch
          on-site via pump.fun as backend — then route ~{FEE_PERCENT}% of
          creator / trading fee rewards toward verified microgrants. Explore
          lists coins launched from this site only.
        </p>
      </div>

      <div className="explore-toolbar">
        <div className="search-pill">
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            placeholder="Search launches"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search launches"
          />
        </div>
        <button type="button" className="btn ghost pill" onClick={onCreate}>
          + Create
        </button>
        <button
          type="button"
          className="btn ghost pill"
          onClick={() => void load()}
        >
          Refresh
        </button>
      </div>

      <section className="graduated-section" aria-label="Launches">
        <div className="graduated-head">
          <h2>Launches</h2>
          <span className="count">{filtered.length}</span>
        </div>
        <p className="graduated-sub">
          Tokens created on EXFUND — newest first. Links open pump.fun (viewer)
          and Solscan.
        </p>

        {error && (
          <div className="alert warn" role="status">
            {error}
          </div>
        )}

        {launches === null ? (
          <p className="muted">Loading launches…</p>
        ) : filtered.length === 0 ? (
          <div className="empty-launches">
            <h3>No launches yet — Create the first one</h3>
            <p>
              Connect a wallet on Create and launch on-site. Your coin appears
              here after the registry records the mint.
            </p>
            <button type="button" className="btn primary" onClick={onCreate}>
              Create token
            </button>
          </div>
        ) : (
          <div className="token-grid">
            {filtered.map((l) => (
              <article className="token-card" key={l.mint}>
                <div className="token-card-media">
                  {l.imageUri ? (
                    <img src={l.imageUri} alt="" loading="lazy" />
                  ) : (
                    <div className="token-placeholder">
                      {l.symbol.slice(0, 2)}
                    </div>
                  )}
                  <div className="token-badges">
                    <span className="badge-pill">Live</span>
                    {l.twitterFeeHandle ? (
                      <span className="badge-pill v2">
                        @{l.twitterFeeHandle}
                      </span>
                    ) : (
                      <span className="badge-pill v2">Solana</span>
                    )}
                  </div>
                </div>
                <div className="token-card-body">
                  <div className="tname">{l.name}</div>
                  <div className="tticker">${l.symbol}</div>
                  <div className="tstats">
                    {relativeTime(l.createdAt)}
                    {l.twitterFeeBps
                      ? ` · ${(l.twitterFeeBps / 100).toFixed(0)}% → @${l.twitterFeeHandle}`
                      : ''}
                  </div>
                </div>
                <div className="token-card-foot">
                  <a
                    href={l.pumpUrl || `https://pump.fun/coin/${l.mint}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    pump.fun
                  </a>
                  <a
                    href={`https://solscan.io/token/${l.mint}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Solscan
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <div className="explore-cta-card">
        <div>
          <h3>Ready to launch?</h3>
          <p>
            Name, ticker, description, optional logo + optional X fee share —
            then Connect and Launch from this site.
          </p>
        </div>
        <button type="button" className="btn primary" onClick={onCreate}>
          Create token
        </button>
      </div>
    </>
  )
}
